import { useFormContext } from "react-hook-form";

import BeforeAfterCommandsList from "components/BeforeAfterCommands/List";
import { ColumnId } from "components/DragDropList/types";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import { HookType, PhaseName, toCommands } from "utils/hooks";
import { StackContext } from "views/Stack/Context";

import { StackHooksFormData } from "../../types";
import ListTitle from "../ListTitle";

type StackHooksListProps = {
  name: PhaseName;
  type: HookType;
  hooksIds: string[];
  allTextCommandsById: Record<string, string>;
  isDragActive: boolean;
};

const StackHooksList = ({
  name,
  type,
  hooksIds,
  isDragActive,
  allTextCommandsById,
}: StackHooksListProps) => {
  const { canUpdateStack } = useTypedContext(StackContext);
  const form = useFormContext<StackHooksFormData>();
  const currentHooks = form.watch(`hooks.${name}.${type}`);

  const handleAddCommands = (type: HookType, lines: string[]) => {
    form.setValue(`hooks.${name}.${type}`, currentHooks?.concat(toCommands(lines)) ?? []);
  };

  const handleRemoveCommand = (type: HookType, id: string) => {
    form.setValue(`hooks.${name}.${type}`, currentHooks?.filter((hook) => hook.id !== id) ?? []);
  };

  const handleEditCommand = (type: HookType, id: string, text: string) => {
    form.setValue(
      `hooks.${name}.${type}`,
      currentHooks?.map((command) => (command.id === id ? { ...command, text } : command)) ?? []
    );
  };

  return (
    <BeforeAfterCommandsList
      columnId={type === "before" ? ColumnId.First : ColumnId.Second}
      isDropZoneActive={isDragActive}
      type={type}
      title={
        <ListTitle
          title="Stack hooks"
          tooltipBody={
            <>
              Stack hooks can be edited as well as their priority changed within stack. In the
              before phase their priority is secondary to the context hooks in opposite to the after
              phase.
              <br />
              <ReadMoreDocsLink
                // TODO: Add docs about hook ordering
                docsUrl={getDocsUrl("/concepts/stack/stack-settings#note-on-hook-order")}
              />
            </>
          }
        />
      }
      allTextCommandsById={allTextCommandsById}
      commandsIds={hooksIds}
      onAddCommands={handleAddCommands}
      onRemoveCommand={handleRemoveCommand}
      onEditCommand={handleEditCommand}
      readOnly={!canUpdateStack.result}
    />
  );
};

export default StackHooksList;
